import { isNil } from 'lodash';
import { useCallback, useState } from 'react';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBTextInput } from '@shared/components/textInput';
import { API } from '@shared/types/api';
import { WorkflowComponentProps } from './WorkflowComponent';
import { WorkflowComponentWrapper } from './WorkflowComponentWrapper';

type Datum = string | undefined;
type Data = Datum | Datum[];

export function WorkflowTextComponent({
  initialData,
  component,
  onDataChanged,
  isReadOnly,
  showLabel,
  paddingHorizontal,
  iconPad,
  showIcon,
}: WorkflowComponentProps) {
  const textComponent = component as API.TextComponent;
  const [value, setValue] = useState<string | null>(formatData(initialData));
  const readOnly = isReadOnly || textComponent.readonly;

  const onChange = useCallback(
    (data: string) => {
      setValue(data);
      // @ts-expect-error -- strict migration
      onDataChanged(textComponent.id, data);
    },
    [onDataChanged, textComponent],
  );

  if (!showLabel && !showIcon) {
    return (
      <WorkflowTextContent
        // @ts-expect-error -- strict migration
        readOnly={readOnly}
        // @ts-expect-error -- strict migration
        value={value}
        initialData={initialData}
        onChange={onChange}
        textComponent={textComponent}
      />
    );
  }

  return (
    <WorkflowComponentWrapper
      // @ts-expect-error -- strict migration
      paddingHorizontal={paddingHorizontal}
      // @ts-expect-error -- strict migration
      iconPad={iconPad}
      component={textComponent}
      // @ts-expect-error -- strict migration
      showLabel={showLabel}
      showIcon={showIcon}
    >
      <WorkflowTextContent
        // @ts-expect-error -- strict migration
        readOnly={readOnly}
        // @ts-expect-error -- strict migration
        value={value}
        initialData={initialData}
        onChange={onChange}
        textComponent={textComponent}
      />
    </WorkflowComponentWrapper>
  );
}

function WorkflowTextContent({
  readOnly,
  value,
  initialData,
  onChange,
  textComponent,
}: {
  readOnly: boolean;
  value: Data;
  initialData: Data;
  onChange: (data: string) => void;
  textComponent: API.TextComponent;
}) {
  return (
    <>
      {!readOnly && (
        <View>
          <MBTextInput
            noBorder
            noPadding
            // @ts-expect-error -- strict migration
            value={formatData(value)}
            onChangeText={onChange}
            // @ts-expect-error -- strict migration
            placeholder={textComponent.placeholder}
          />
        </View>
      )}
      {readOnly && (
        <View fullWidth>
          <Text size="body1" inactive={isEmptyData(initialData)}>
            {formatData(initialData) || textComponent.label}
          </Text>
        </View>
      )}
    </>
  );
}

function formatData(data: Data): string | null {
  if (Array.isArray(data)) {
    const displayableData = data.filter(isDisplayableDatum);
    return displayableData.length > 0 ? displayableData.join(', ') : null;
  }

  // @ts-expect-error -- strict migration
  return isDisplayableDatum(data) ? data : null;
}

function isDisplayableDatum(datum: Datum): boolean {
  return !isNil(datum) && datum.length > 0;
}

function isEmptyData(data: Data): boolean {
  if (Array.isArray(data)) {
    return !data.some(isDisplayableDatum);
  }

  return !isDisplayableDatum(data);
}
